import Vue from 'vue';
import axios from 'axios';
import VueAxios from 'vue-axios';
import TokenService from '@/core/services/oauth.service';
import appParameters, { CONTEXT_WEBSITE, CONTEXT_MOBILE_APP } from '@/core/config/parameters';
import HttpUtil from '@/core/utils/http';

export const { APP_BASE, API_BASE, API_CLIENT_ID, API_CLIENT_SECRET, APP_CONTEXT } = appParameters;

import axiosDefaults from 'axios/lib/defaults';

/**
 * Service to call HTTP request via Axios
 */
const ApiService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = API_BASE;
    this.setDefaultHeaders();
    // do not send credentials by default
    this.setWithCredentials(true);
    this.setInterceptors();
  },

  setInterceptors() {
    Vue.axios.interceptors.request.use(function (config) {
      // Do something before each request is sent

      // BEGIN: fetch the access token and put it into the appropriate header before EACH request
      const accessToken = TokenService.getToken();
      if (accessToken) {
        config.headers[
          'Authorization'
          ] = `Bearer ${accessToken}`;
      } else {
        // remove any other potential value which may be there,
        // e.g. basic auth credentials which are there when we secure the server with basic auth for testing
        // Our oAuth 2 server will misinterpret this header and try to prematurely find an oauth client during
        // regular oAuth authorization code flow
        // This can be considered a quick hack but useful
        delete config.headers['Authorization'];
      }
      // END: fetch the access token and put it into the appropriate header before EACH request

      return config;
    }, function (error) {
      // Do something with request error
      return Promise.reject(error);
    });
  },

  /**
   *
   * @private
   */
  setWithCredentials(flag) {
    // Globally set some IMPORTANT defaults for Axios
    // These settings will be used for each requests in all vue components which will be called after this point
    // necessary to be able to reach the API (/webapi/...) from webpack dev server (cross domain request)
    // and pass the session cookie along
    if (flag) {
      axiosDefaults.withCredentials = true;
      axiosDefaults.credentials = 'same-origin';
    } else {
      axiosDefaults.withCredentials = false;
      axiosDefaults.credentials = 'same-origin';
    }
  },

  makeApiUrl(url) {
    if (!HttpUtil.isAbsoluteUrl(url)) {
      // create an absolute URL to the API base
      return `${API_BASE}/${url.replace(/^\/+/g, '')}`;
    } else {
      // we have already an absolute URL so we just return that even if we do not hit our API with that
      // the correct URL should be assembled upstream
      return url;
    }
  },

  makeApiImagePath(imagePath) {
    if (!HttpUtil.isAbsoluteUrl(imagePath)) {
      // create an absolute URL to the API base
      return `${API_BASE}/${imagePath.replace(/^\/+/g, '')}`;
    } else {
      // we have already an absolute URL so we just return that even if we do not hit our API with that
      // the correct URL should be assembled upstream
      return imagePath;
    }
  },

  /**
   * Set the default HTTP request headers
   */
  setDefaultHeaders() {
    // We need this header for all AJAX requests done with Axios in order
    // for symfony to be able to identify an AJAX request on the backend side
    Vue.axios.defaults.headers.common[
      'X-Requested-With'
      ] = 'XMLHttpRequest';
  },

  query(resource, params) {
    resource = this.makeApiUrl(resource);
    return Vue.axios.get(resource, params).catch((error) => {
      // console.log(error);
      throw new Error(`[KT] ApiService ${error}`);
    });
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @param parameters
   * @param headers
   * @returns {*}
   */
  get(resource, slug = '', parameters = {}, headers = {}) {
    resource = this.makeApiUrl(resource);
    let resourceUrl;
    if (slug > '') {
      resourceUrl = `${resource}/${slug}`;
    } else {
      resourceUrl = `${resource}`;
    }
    return Vue.axios.get(resourceUrl, {
      params: parameters,
      headers: headers
    }).catch(error => {
      console.error(`[KT] ApiService ${error}`);
      throw error;
    });
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */
  post(resource, params) {
    resource = this.makeApiUrl(resource);
    return Vue.axios.post(`${resource}`, params);
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    resource = this.makeApiUrl(resource);
    return Vue.axios.put(`${resource}/${slug}`, params);
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  put(resource, params) {
    resource = this.makeApiUrl(resource);
    return Vue.axios.put(`${resource}`, params);
  },

  /**
   * Send the PATCH HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  patch(resource, params) {
    resource = this.makeApiUrl(resource);
    return Vue.axios.patch(`${resource}`, params);
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource) {
    resource = this.makeApiUrl(resource);
    return Vue.axios.delete(resource).catch((error) => {
      // console.log(error);
      throw new Error(`[RWV] ApiService ${error}`);
    });
  },

  /**
   * Translates a given route to public (/appapi/...) OR private (/webapi/...)
   * according to the context the app runs in
   * website => private => /webapi/...
   * mobile app => public => /appapi/...
   * We share a lot of code between the hybrid app and website so tthis helper is usefull to keep
   * the codebase the same
   */
  apiRoute(route) {
    let newRoute = route;
    if (APP_CONTEXT === CONTEXT_MOBILE_APP) {
      newRoute = route.replace(/^webapi_/i, 'appapi_');
    } else {
      newRoute = route.replace(/^appapi_/i, 'webapi_');
    }
    return newRoute;
  }
};

export default ApiService;
